import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, MessageSquare, FolderSync, FileText, ShieldCheck, Search, Link2, ArrowRight } from 'lucide-react';

// 🌏 해외 홍보용 영문 랜딩 — dentalcluster.com/global (가입 CTA → /join/global)
function GlobalLanding() {
    const navigate = useNavigate();
    return (
        <div style={S.page}>
            <header style={S.header}>
                <div style={S.brand}><Globe size={20} color="#4F52C7" /> DentCluster <span style={{ color: '#4F52C7' }}>Global</span></div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button style={S.ghostBtn} onClick={() => navigate('/login')}>Sign in</button>
                    <button style={S.primarySm} onClick={() => navigate('/join/global')}>Apply</button>
                </div>
            </header>

            <section style={S.hero}>
                <h1 style={S.h1}>Outsource to verified<br />Korean dental labs</h1>
                <p style={S.lede}>
                    Browse Korean labs at a glance and pick the right one for your design work.
                    Drop a case folder in — <b>the order form writes itself and files transfer automatically.
                    No uploads, no email attachments</b>, and monthly invoices are generated for you.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button style={S.primary} onClick={() => navigate('/join/global')}>Apply for an account <ArrowRight size={16} /></button>
                    <button style={S.ghostBtn} onClick={() => navigate('/login')}>I already have one</button>
                </div>
                <p style={{ fontSize: 12.5, color: '#94A3B8', marginTop: 14 }}>Free for overseas labs · Applications reviewed within 1–2 business days</p>
            </section>

            <section style={S.section}>
                <h2 style={S.h2}>How it works</h2>
                <div style={S.steps}>
                    <div style={S.step}>
                        <div style={S.stepIcon}><Search size={20} /></div>
                        <div style={S.stepTitle}>1. Compare labs &amp; pick yours</div>
                        <p style={S.stepBody}><b>See Korea's dental labs at a glance</b> — specialties, equipment, portfolio and turnaround — and choose the right lab for your design work. Discuss cases and pricing in chat; <b>tap Translate to read any message in your language</b>.</p>
                    </div>
                    <div style={S.step}>
                        <div style={S.stepIcon}><Link2 size={20} /></div>
                        <div style={S.stepTitle}>2. Connect folders</div>
                        <p style={S.stepBody}>Install the DentCluster Agent and link a sending folder with your lab. One-time setup, approved by both sides.</p>
                    </div>
                    <div style={S.step}>
                        <div style={S.stepIcon}><FolderSync size={20} /></div>
                        <div style={S.stepTitle}>3. Drop a case — everything else is automatic</div>
                        <p style={S.stepBody}>Put a case folder in and you're done: <b>the order form is written automatically</b> from your CAD files and travels with the case — <b>no uploads, no downloads</b>. Finished designs return to your folder by themselves, and <b>confirmed cases flow straight into an auto-generated monthly invoice</b>.</p>
                    </div>
                </div>
            </section>

            <section style={S.section}>
                <h2 style={S.h2}>Why labs use it</h2>
                <div style={S.grid}>
                    <div style={S.card}><ShieldCheck size={18} color="#4F52C7" /><b>Verified partners</b><span>Every Korean lab is a registered business on DentCluster; overseas accounts are reviewed before activation.</span></div>
                    <div style={S.card}><MessageSquare size={18} color="#4F52C7" /><b>Translated chat</b><span>Write in your language, they read in theirs. Tooth numbers and shade codes stay untouched.</span></div>
                    <div style={S.card}><FolderSync size={18} color="#4F52C7" /><b>Zero-upload workflow</b><span>Order forms are auto-written from your CAD files, and case files &amp; results move by themselves. Patient names never leave your PC — cases travel under case numbers.</span></div>
                    <div style={S.card}><FileText size={18} color="#4F52C7" /><b>Invoices write themselves</b><span>Statements are generated automatically from your confirmed cases — monthly, in your currency. You pay the lab directly; the platform fee is paid by the Korean lab, not you.</span></div>
                </div>
            </section>

            <section style={{ ...S.section, textAlign: 'center', paddingBottom: 70 }}>
                <h2 style={S.h2}>Ready to try your first case?</h2>
                <button style={{ ...S.primary, margin: '14px auto 0' }} onClick={() => navigate('/join/global')}>Apply for an account <ArrowRight size={16} /></button>
            </section>

            <footer style={S.footer}>
                <span>© DentCluster · Korea</span>
                <span>dentcluster@gmail.com</span>
            </footer>
        </div>
    );
}

const S = {
    page: { minHeight: '100vh', background: '#FAFAFC', color: '#1E2230', fontFamily: "-apple-system, 'Segoe UI', sans-serif" },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', maxWidth: 1000, margin: '0 auto' },
    brand: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 17, fontWeight: 800 },
    hero: { textAlign: 'center', padding: '60px 20px 50px', maxWidth: 760, margin: '0 auto' },
    h1: { fontSize: 'clamp(30px, 5vw, 46px)', fontWeight: 800, lineHeight: 1.2, margin: 0 },
    lede: { fontSize: 17, color: '#5A6072', lineHeight: 1.7, margin: '18px auto 26px', maxWidth: 560 },
    h2: { fontSize: 24, fontWeight: 800, textAlign: 'center', margin: '0 0 26px' },
    section: { maxWidth: 960, margin: '0 auto', padding: '40px 20px' },
    steps: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 },
    step: { background: '#fff', border: '1px solid #E4E6EE', borderRadius: 14, padding: '22px 20px' },
    stepIcon: { width: 40, height: 40, borderRadius: 10, background: '#EEF0FA', color: '#4F52C7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
    stepTitle: { fontSize: 16, fontWeight: 800, marginBottom: 6 },
    stepBody: { fontSize: 13.5, color: '#5A6072', lineHeight: 1.65, margin: 0 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
    card: { background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12, padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, color: '#1E2230', lineHeight: 1.5 },
    primary: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '13px 22px', background: '#4F52C7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15.5, fontWeight: 700, cursor: 'pointer' },
    primarySm: { padding: '9px 16px', background: '#4F52C7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    ghostBtn: { padding: '9px 16px', background: '#fff', color: '#5A6072', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    footer: { display: 'flex', justifyContent: 'space-between', maxWidth: 1000, margin: '0 auto', padding: '18px 24px', borderTop: '1px solid #E4E6EE', fontSize: 12.5, color: '#94A3B8' },
};

export default GlobalLanding;
